import { Link } from "react-router-dom";
import { KRP_SERVICE_AREAS } from "../../../mocks/krp-data";
import { useScrollRevealChildren } from "../../../hooks/useScrollReveal";

export interface ServiceAreasContent {
  badge?: string;
  headline: string;
  body: string;
  ctaLabel: string;
  ctaPath: string;
}

interface ServiceAreasSectionProps {
  content: ServiceAreasContent;
}

const COUNTIES = [
  {
    name: "Palm Beach County",
    cities: ["Boca Raton", "Delray Beach", "Palm Beach Gardens", "Wellington", "Boynton Beach", "Lake Worth", "West Palm Beach", "Jupiter"],
  },
  {
    name: "Broward County",
    cities: ["Fort Lauderdale", "Pompano Beach", "Coral Springs", "Parkland", "Deerfield Beach"],
  },
  {
    name: "Miami-Dade County",
    cities: ["Highland Beach", "Lantana"],
  },
];

export default function ServiceAreasSection({ content }: ServiceAreasSectionProps) {
  const { badge, headline, body, ctaLabel, ctaPath } = content;
  const ref = useScrollRevealChildren();

  return (
    <section className="relative py-28 overflow-hidden bg-[#0E0E0E]">
      {/* Background image */}
      <div className="absolute inset-0">
        <div
          className="absolute inset-0 bg-cover bg-center scale-105"
          style={{
            backgroundImage:
              "url(https://public.readdy.ai/ai/img_res/41800ee03481b2ae0983972896fc1935.jpg)",
          }}
        />
        <div className="absolute inset-0 bg-[#0E0E0E]/92" />
        {/* Subtle repeating line texture */}
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: "repeating-linear-gradient(90deg, rgba(201,169,97,0.5) 0px, rgba(201,169,97,0.5) 1px, transparent 1px, transparent 80px)",
          }}
        />
      </div>

      <div ref={ref} className="relative z-10 max-w-screen-xl mx-auto px-6 xl:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">

          {/* Left column */}
          <div className="lg:col-span-5">
            {badge && (
              <div className="reveal flex items-center gap-4 mb-7">
                <div className="h-px w-12 bg-[#C9A961]" />
                <span
                  className="text-[#C9A961] text-[10px] tracking-[0.3em] uppercase font-medium"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  {badge}
                </span>
              </div>
            )}
            <h2
              className="reveal delay-100 text-4xl md:text-5xl xl:text-[56px] text-white leading-tight mb-7"
              style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700 }}
            >
              {headline}
            </h2>
            <p
              className="reveal delay-200 text-white/45 text-base leading-[1.85] mb-10"
              style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}
            >
              {body}
            </p>

            {/* CTAs */}
            <div className="reveal delay-300 flex flex-col sm:flex-row gap-4 mb-12">
              <Link
                to={ctaPath}
                className="inline-flex items-center gap-3 bg-[#C9A961] text-white text-[10px] tracking-[0.22em] uppercase font-semibold px-9 py-4 hover:bg-[#b8964f] transition-all duration-300 cursor-pointer whitespace-nowrap"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                {ctaLabel}
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-sm" />
                </div>
              </Link>
              <a
                href="tel:5614416008"
                className="inline-flex items-center gap-3 border border-white/20 text-white text-[10px] tracking-[0.22em] uppercase font-medium px-9 py-4 hover:border-[#C9A961] hover:text-[#C9A961] transition-all duration-300 cursor-pointer whitespace-nowrap"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-phone-line text-sm" />
                </div>
                (561) 441-6008
              </a>
            </div>

            {/* Showroom address block */}
            <div className="reveal delay-400 flex items-start gap-4 border-t border-white/6 pt-8">
              <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 bg-[#C9A961]/10 border border-[#C9A961]/20">
                <i className="ri-store-2-line text-[#C9A961] text-base" />
              </div>
              <div>
                <p
                  className="text-white/65 text-xs font-semibold mb-1 tracking-wide"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  KRP Design Showroom — Boca Center
                </p>
                <p className="text-white/30 text-[11px]" style={{ fontFamily: "'Inter', sans-serif" }}>
                  5250 Town Center Cir #109, Boca Raton, FL 33486
                </p>
                <p className="text-white/30 text-[11px] mt-0.5" style={{ fontFamily: "'Inter', sans-serif" }}>
                  Mon–Fri 10AM–5PM · By Appointment
                </p>
              </div>
            </div>
          </div>

          {/* Right column — county-grouped areas */}
          <div className="lg:col-span-7 reveal-right delay-200">
            <div className="space-y-8">
              {COUNTIES.map((county, cIdx) => (
                <div key={county.name} className={`reveal delay-${cIdx * 150 + 100}`}>
                  {/* County header */}
                  <div className="flex items-center gap-4 mb-4">
                    <div className="h-px flex-1 bg-white/6" />
                    <span
                      className="text-[#C9A961]/50 text-[9px] tracking-[0.3em] uppercase whitespace-nowrap"
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    >
                      {county.name}
                    </span>
                    <div className="h-px flex-1 bg-white/6" />
                  </div>

                  {/* Cities list */}
                  <div className="flex flex-wrap gap-2">
                    {county.cities.map((city) => (
                      <Link
                        key={city}
                        to="/locations"
                        className={`group flex items-center gap-2 px-4 py-2.5 text-xs cursor-pointer transition-all duration-300 whitespace-nowrap ${
                          city === "Boca Raton"
                            ? "bg-[#C9A961]/15 border border-[#C9A961]/40 text-[#C9A961] hover:bg-[#C9A961]/25"
                            : "bg-white/[0.04] border border-white/8 text-white/50 hover:bg-white/10 hover:border-[#C9A961]/30 hover:text-white/80"
                        }`}
                        style={{ fontFamily: "'Inter', sans-serif" }}
                      >
                        <div className="w-3 h-3 flex items-center justify-center">
                          <i
                            className={`ri-map-pin-2-fill text-[10px] ${
                              city === "Boca Raton" ? "text-[#C9A961]" : "text-white/25 group-hover:text-[#C9A961]/60"
                            } transition-colors`}
                          />
                        </div>
                        {city}
                        {city === "Boca Raton" && (
                          <span
                            className="text-[8px] text-[#C9A961]/60 tracking-widest uppercase"
                            style={{ fontFamily: "'Inter', sans-serif" }}
                          >
                            HQ
                          </span>
                        )}
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Large service areas from mock (catch overflow) */}
            <div className="mt-10 p-6 border border-white/6 bg-white/[0.02]">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <p
                    className="text-white/50 text-xs font-semibold mb-1"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    Serving {KRP_SERVICE_AREAS.length} communities across South Florida
                  </p>
                  <p className="text-white/25 text-[11px]" style={{ fontFamily: "'Inter', sans-serif" }}>
                    Not sure if we serve your area? Call us — we&apos;ll confirm.
                  </p>
                </div>
                <a
                  href="tel:5614416008"
                  className="flex items-center gap-2 text-[#C9A961] text-[11px] font-semibold hover:text-[#b8964f] transition-colors cursor-pointer whitespace-nowrap"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-phone-line text-sm" />
                  </div>
                  (561) 441-6008
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
